import React from 'react'
import styled, { keyframes } from 'styled-components'
import { DSButton, DSFlex, DSSpacer, DSText, type DSGridSpacing } from '@zoe/ds-web'
import { onNextClickType } from 'components/types'
import { ContextAwareText } from '../core/text/ContextAwareText'
import { AgeRange, Quizzes } from '../../redux/quiz/types'
import { GifName, resolveTopImage } from '../../data/gifs'
import { ScreenContainer } from '../core/layout/ScreenContainer'
import { StickyFooter, StickyFooterContent } from '../core/layout/StickyFooter'
import { SCREEN_MAX_WIDTH } from '../core/constants'

export type SocialProofScreenProps = {
  title: string
  description?: string
  topImage?: string
  topImageAlt?: string
  topImageAspectRatio?: string
  gif?: GifName
  buttonText?: string
  /** 'large' bumps the headline from 30px to 36px (e9). Defaults to the e4-e8 size. */
  // TODO: FormScreen's titleVariant takes a DSText variant; align the two contracts.
  titleVariant?: 'default' | 'large'
  /** Fill for the laurel leaves. Defaults to the e4-e8 gold. */
  leafColor?: string
  /** Renders topImage edge-to-edge rather than inset by the container padding (e9). */
  topImageBleed?: boolean
  /**
   * Gap between topImage and the headline. Defaults to 0 — e4-e8 rely on the
   * transparent margin baked into their collage instead. e9's design puts 55px
   * of real space here, so it sets this explicitly.
   */
  topImageSpacing?: DSGridSpacing
  /**
   * Gap between the headline and the description. Defaults to 16, the e4-e8
   * spacing; e9's design uses 8.
   */
  descriptionSpacing?: DSGridSpacing
  /** Horizontal padding (px) around the subtitle, so it wraps as in the design. */
  descriptionPaddingX?: number
  /** Pulls the image up under the header (e10: 12px rather than the default 24px). */
  tightTop?: boolean
  /**
   * Places the laurels this far from the headline instead of at the container's
   * edges. The row also spans the full screen width, since at 36px the headline
   * leaves too little room inside the 24px page padding.
   */
  laurelGap?: DSGridSpacing
  /** Laurel width in px (height follows the 26x54 artwork). Defaults to 26. */
  laurelSize?: number
  /** e12: no laurels either side of the headline. */
  hideLaurels?: boolean
  /** e12: centres the image and headline in the space above the button. */
  centerContent?: boolean
  /** Copy that varies with the user's age range, e.g. { number: { '18_34': '36,000+', …, unknown: '340,000+' } } for {number}. */
  ageTokens?: Record<string, AgeCopy>
}

// One string per age range, plus `unknown` for when the age wasn't answered.
export type AgeCopy = Partial<Record<AgeRange, string>> & { unknown: string }

const resolveAgeTokens = (age: AgeRange | undefined, tokens: Record<string, AgeCopy> | undefined) =>
  tokens && Object.fromEntries(Object.entries(tokens).map(([name, copy]) => [name, (age && copy[age]) ?? copy.unknown]))

const DEFAULT_LEAF_COLOR = '#CAB89C'

/* A 36px headline plus real gaps does not fit inside the page's 24px padding,
   so the laurel row reclaims 20px of it on each side; the leaves then sit about
   4px from the screen edge. */
const HeadlineRow = styled.div<{ $bleed?: boolean }>`
  ${({ $bleed }) =>
    $bleed
      ? `margin-left: calc(-1 * var(--grid-24) + 4px);
         margin-right: calc(-1 * var(--grid-24) + 4px);`
      : ''}
`

/* Fills the space between the header and the button, so the content can be
   centred in it. */
const Root = styled.div<{ $center?: boolean }>`
  ${({ $center }) => ($center ? 'flex: 1; display: flex; flex-direction: column; justify-content: center;' : '')}
`

/* Cancels 12px of ScreenContainer's 24px top padding. */
const TightTop = styled.div`
  margin-top: -12px;
`

type Props = SocialProofScreenProps & {
  onNextClick: onNextClickType
  quiz: Quizzes
}

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const leafIn = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-24px) rotate(-25deg) scale(0.6);
  }
  60% {
    opacity: 1;
  }
  100% {
    opacity: 1;
    transform: translateX(0) rotate(0deg) scale(1);
  }
`

const leafInRight = keyframes`
  0% {
    opacity: 0;
    transform: scaleX(-1) translateX(-24px) rotate(-25deg) scale(0.6);
  }
  60% {
    opacity: 1;
  }
  100% {
    opacity: 1;
    transform: scaleX(-1) translateX(0) rotate(0deg) scale(1);
  }
`

const FadeInUp = styled.div<{ $delay?: number }>`
  animation: ${fadeInUp} 0.6s ease-out ${({ $delay = 0 }) => $delay}s both;
`

const LeafScale = styled.div<{ $delay?: number; $flip?: boolean }>`
  flex-shrink: 0;
  transform-origin: center;
  animation: ${({ $flip }) => ($flip ? leafInRight : leafIn)} 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)
    ${({ $delay = 0 }) => $delay}s both;
`

const TopImage = styled.img.attrs({ loading: 'lazy' })`
  display: block;
  width: 100%;
  object-fit: cover;
  border-radius: var(--radius-12);
  animation: ${fadeInUp} 0.6s ease-out both;
`

/* Edge-to-edge variant (e9): escapes ScreenContainer's horizontal padding so the
   collage spans the full viewport, matching the 375-wide IMG_Large frame in the
   design. Unlike the welcome hero this stays below the header, so there is no
   vertical offset. Offsets with margins rather than `transform`, which the
   fadeInUp animation owns. */
// TODO: near-duplicate of FormScreen's BleedTopImage; extract a shared helper.
const BleedTopImage = styled(TopImage)`
  border-radius: 0;

  /* Mirrors ScreenContainer: below its max-width the container carries 24px of
     horizontal padding to escape; above it that padding is already 0. */
  /* TODO: assumes ScreenContainer's default maxWidth; a custom one desyncs this. */
  @media (max-width: ${SCREEN_MAX_WIDTH}px) {
    width: calc(100% + 2 * var(--grid-24));
    margin-left: calc(-1 * var(--grid-24));
  }
`

const LEAF_WIDTH = 26
const LEAF_ASPECT = 54 / 26

const BayLeafIcon: React.FC<{ color?: string; size?: number }> = ({
  color = DEFAULT_LEAF_COLOR,
  size = LEAF_WIDTH,
}) => (
  <svg
    width={size}
    height={Math.round(size * LEAF_ASPECT)}
    viewBox="0 0 19 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M12.8622 36.4826C11.7012 36.3796 10.2772 36.7607 9.28806 37.4266C8.93796 37.6701 8.89756 38.0368 9.1938 38.3418C10.0571 39.2126 11.4272 39.861 12.6088 39.9711C13.8442 40.118 15.292 39.6959 16.2535 38.8966C16.5299 38.6531 16.5362 38.3275 16.2804 38.074C15.4266 37.1961 14.0533 36.5616 12.8622 36.4826ZM16.5485 30.2599C15.8658 31.2394 15.4502 32.6854 15.5142 33.8715C15.5887 35.1229 16.1763 36.528 16.9935 37.3666C17.2297 37.6265 17.5574 37.6265 17.7935 37.353C18.6281 36.4418 19.1153 34.9717 18.9766 33.6794C18.899 32.4795 18.2877 31.0882 17.4362 30.1947C17.1393 29.8834 16.7948 29.9109 16.5485 30.2599ZM7.88884 31.5853C6.78881 31.188 5.30066 31.1815 4.15946 31.594C3.76822 31.7347 3.65341 32.0533 3.85218 32.4232C4.46917 33.5032 5.62308 34.5012 6.75401 34.9161C7.88807 35.3962 9.39996 35.344 10.5475 34.7771C10.8643 34.6258 10.9554 34.3348 10.7835 34.0163C10.1974 32.9363 9.04352 31.9656 7.88884 31.5853ZM13.0071 26.532C12.1122 27.2784 11.3354 28.57 11.0851 29.7425C10.8285 30.952 11.0487 32.4422 11.6299 33.4981C11.7955 33.8302 12.1162 33.9057 12.4235 33.7039C13.4538 33.0156 14.2782 31.7345 14.4873 30.4766C14.7067 29.2663 14.4493 27.7585 13.8783 26.7001C13.6788 26.31 13.331 26.2448 13.0071 26.532ZM0.00356082 24.5823C0.131104 25.7958 0.788476 27.1839 1.63905 28.0372C2.4999 28.9491 3.90088 29.5209 5.16009 29.4855C5.52438 29.4816 5.73029 29.2314 5.6899 28.8615C5.56629 27.607 4.89862 26.2599 4.02431 25.475C3.17374 24.6387 1.81318 24.0259 0.61811 23.9413C0.213401 23.9108 -0.0329039 24.1577 0.00356082 24.5823ZM10.7298 22.9489C9.59499 23.253 8.3659 24.1158 7.67765 25.0962C6.96885 26.0733 6.58152 27.5474 6.68683 28.7633C6.71059 29.1333 6.97747 29.3286 7.33149 29.2708C8.57406 29.0455 9.84676 28.2099 10.5017 27.1436C11.1939 26.1471 11.578 24.6698 11.4418 23.4491C11.4212 23.0213 11.1274 22.8331 10.7298 22.9489ZM7.40594 18.8163C6.4437 19.598 5.69845 20.921 5.51703 22.1135C5.45999 22.4666 5.65562 22.72 6.00647 22.7545C7.2451 22.8654 8.69675 22.39 9.61857 21.5328C10.5404 20.7271 11.2889 19.3937 11.4869 18.177C11.5305 17.7692 11.3079 17.5055 10.9167 17.5192C9.69468 17.5281 8.28342 18.0452 7.40594 18.8163ZM0.102501 16.3353C-0.0804088 17.5215 0.20554 19.0261 0.826445 20.102C1.44418 21.2127 2.6701 22.1375 3.87938 22.4038C4.21995 22.489 4.49317 22.304 4.55415 21.9445C4.72361 20.7583 4.42023 19.2642 3.77241 18.2019C3.1586 17.2118 1.99048 16.2733 0.872279 15.8656C0.470746 15.7119 0.163471 15.8937 0.102501 16.3353ZM8.45137 12.6974C7.30379 13.081 6.14672 14.0444 5.55668 15.1108C5.37135 15.426 5.48617 15.7413 5.78951 15.8989C6.90302 16.4553 8.42519 16.5076 9.59331 16.021C10.7472 15.5723 11.9012 14.5711 12.4848 13.4975C12.6836 13.1413 12.5553 12.8188 12.1641 12.6748C11.0403 12.2832 9.55534 12.2897 8.45137 12.6974ZM2.43182 7.81871C1.84028 8.90442 1.62408 10.4155 1.84662 11.6017C2.06282 12.8634 2.88011 14.1549 3.92389 14.833C4.22797 15.0381 4.52815 14.9456 4.7 14.6271C5.29155 13.5615 5.5014 12.0569 5.24162 10.8643C4.98504 9.66444 4.20812 8.3833 3.30614 7.63697C2.99251 7.38363 2.63455 7.45923 2.43182 7.81871ZM11.7328 7.44061C10.5417 7.6666 9.2729 8.47241 8.53718 9.4639C8.33521 9.74137 8.39936 10.0638 8.67895 10.2521C9.71167 10.9693 11.2101 11.217 12.4186 10.8808C13.5963 10.617 14.8785 9.7976 15.6205 8.85109C15.8732 8.5117 15.7957 8.16914 15.4282 7.98736C14.3788 7.43096 12.9073 7.21787 11.7328 7.44061ZM6.45457 1.75122C5.73156 2.75489 5.2786 4.22497 5.34591 5.4457C5.35939 6.67612 6.00798 8.08105 6.94401 8.92308C7.22044 9.17646 7.55147 9.13864 7.76055 8.84429C8.49067 7.85828 8.91991 6.39865 8.82881 5.19161C8.74091 3.99175 8.14299 2.60371 7.34236 1.7102C7.04538 1.40217 6.70798 1.41906 6.45457 1.75122ZM15.9479 0.0050404C14.7734 0.148227 13.4262 0.802075 12.5788 1.6521C11.718 2.51258 11.101 3.90063 11.0313 5.09323C11.0043 5.46318 11.2102 5.69639 11.5746 5.69318C12.8203 5.69071 14.1976 5.0642 15.0347 4.12169C15.8654 3.26121 16.4823 1.86273 16.5925 0.635536C16.6122 0.210902 16.3692 -0.0392064 15.9479 0.0050404Z"
      fill={color}
    />
  </svg>
)

export const SocialProofScreen: React.FC<Props> = (props) => {
  const {
    title,
    description,
    topImage,
    topImageAlt,
    topImageAspectRatio,
    buttonText,
    titleVariant,
    leafColor,
    topImageBleed,
    topImageSpacing,
    descriptionSpacing = 16,
    descriptionPaddingX,
    tightTop,
    laurelGap,
    laurelSize,
    hideLaurels,
    centerContent,
    ageTokens,
    onNextClick,
    quiz,
  } = props

  const image = resolveTopImage(props.gif, { topImage, topImageAlt, topImageAspectRatio })
  const titleTokens = resolveAgeTokens('welcome_age_range' in quiz ? quiz.welcome_age_range : undefined, ageTokens)
  const TopImageComponent = topImageBleed ? BleedTopImage : TopImage

  return (
    <Root $center={centerContent}>
      <ScreenContainer>
        {tightTop && <TightTop />}
        {image.topImage && (
          <>
            <TopImageComponent
              src={image.topImage}
              alt={image.topImageAlt ?? ''}
              style={image.topImageAspectRatio ? { aspectRatio: image.topImageAspectRatio } : undefined}
            />
            {topImageSpacing ? <DSSpacer size={topImageSpacing} direction="vertical" /> : null}
          </>
        )}

        <HeadlineRow $bleed={!!laurelGap}>
          <DSFlex
            direction="row"
            alignItems="center"
            justifyContent={laurelGap ? 'center' : 'space-between'}
            gap={laurelGap}
          >
            {!hideLaurels && (
              <LeafScale $delay={0.1}>
                <BayLeafIcon color={leafColor} size={laurelSize} />
              </LeafScale>
            )}
            <FadeInUp $delay={0.2}>
              <DSText
                as="p"
                variant={titleVariant === 'large' ? 'fluid-heading-700' : 'fluid-heading-600'}
                weight="semiBold"
                align="center"
              >
                <ContextAwareText text={title} quiz={quiz} additionalTokens={titleTokens} />
              </DSText>
            </FadeInUp>
            {!hideLaurels && (
              <LeafScale $delay={0.2} $flip>
                <BayLeafIcon color={leafColor} size={laurelSize} />
              </LeafScale>
            )}
          </DSFlex>
        </HeadlineRow>

        {description && (
          <>
            <DSSpacer size={descriptionSpacing} direction="vertical" />
            <FadeInUp $delay={0.4}>
              <DSText variant="fluid-paragraph-300" color="secondary" align="center">
                <span
                  style={descriptionPaddingX ? { display: 'block', padding: `0 ${descriptionPaddingX}px` } : undefined}
                >
                  <ContextAwareText text={description} quiz={quiz} />
                </span>
              </DSText>
            </FadeInUp>
          </>
        )}

        <StickyFooter>
          <StickyFooterContent>
            <DSButton
              variant="primary"
              colorScheme="default"
              size="large"
              width="full"
              onPress={onNextClick}
              testId="continue-button"
            >
              <DSText variant="fluid-paragraph-200" weight="semiBold">
                {buttonText ?? 'Continue'}
              </DSText>
            </DSButton>
          </StickyFooterContent>
        </StickyFooter>
      </ScreenContainer>
    </Root>
  )
}
